import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { translateDomain } from '../../utils/labels_utils';
import styled from 'styled-components';

const INK = 'var(--vo-ink)';
const MUTED = 'var(--vo-slate)';
const BLUE = 'var(--color-brand)';
const DIVIDER = 'var(--vo-cloud)';
const TEXT = 'var(--vo-ink-2)';
const FADED = 'var(--vo-steel)';

const DomainItem = styled.div`
  border: 1px solid ${DIVIDER};
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 10px;
`;
const DomainHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  background: var(--vo-capri-25);
  cursor: pointer;
  &:hover { background: var(--vo-capri-50); }
`;
const DomainLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;
const DomainName = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${INK};
`;
const DomainBadge = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: ${BLUE};
  background: var(--vo-capri-50);
  padding: 2px 8px;
  border-radius: 10px;
`;
const Chevron = styled.svg`
  transition: transform 200ms;
  transform: ${({ $open }) => $open ? 'rotate(180deg)' : 'rotate(0deg)'};
  flex-shrink: 0;
`;
const CtrlRow = styled.div`
  padding: 10px 18px;
  border-top: 1px solid var(--vo-mist);
  display: flex;
  align-items: flex-start;
  gap: 12px;
  &:hover { background: var(--vo-mist); }
`;
const CtrlCode = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: ${FADED};
  min-width: 72px;
  flex-shrink: 0;
  padding-top: 1px;
`;
const CtrlName = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${TEXT};
  line-height: 1.5;
`;
const EmptyBody = styled.div`
  padding: 12px 18px;
  border-top: 1px solid var(--vo-mist);
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${MUTED};
`;

const DomainControlsList = ({ data }) => {
  const { t } = useTranslation(['gap-analysis', 'common']);
  const [openIds, setOpenIds] = useState([]);

  if (!data || data.length === 0) return null;

  const toggle = id => setOpenIds(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
  );

  return (
    <div>
      {data.map((section, idx) => {
        const id = section.titleKey ?? section.title ?? idx;
        const open = openIds.includes(id);
        const name = translateDomain(t, section.titleKey ?? section.title);
        const count = section.count ?? section.children?.length ?? 0;
        const items = section.children ?? [];

        return (
          <DomainItem key={id}>
            <DomainHeader onClick={() => toggle(id)}>
              <DomainLeft>
                <DomainName>{name}</DomainName>
                <DomainBadge>{count}</DomainBadge>
              </DomainLeft>
              <Chevron
                $open={open}
                width="14" height="14" viewBox="0 0 24 24"
                fill="none" stroke={MUTED} strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round"
              >
                <polyline points="6 9 12 15 18 9" />
              </Chevron>
            </DomainHeader>
            {open && (
              <div>
                {items.length > 0 ? items.map(ctrl => (
                  <CtrlRow key={ctrl.id ?? ctrl._id}>
                    <CtrlCode>{ctrl.id ?? ctrl._id}</CtrlCode>
                    <CtrlName>{ctrl.name}</CtrlName>
                  </CtrlRow>
                )) : (
                  <EmptyBody>{t('common:empty.noItems')}</EmptyBody>
                )}
              </div>
            )}
          </DomainItem>
        );
      })}
    </div>
  );
};

export default DomainControlsList;
